import { FC, useEffect, useMemo, useState } from "react"
import { observer } from "mobx-react"
import { useStore } from "stores/utils/use-store"
import { Button, Card, Form, Input, InputNumber, Modal, Popconfirm, Select, Space, Table, Tag, Typography } from "antd"
import { CUT_DETECT_LEVELS, CUT_LIST_STATUSES, CutDetectLevel, CutTitleRule, CutTitleRuleInput } from "../store/types"


const DETECT_COLOR: Record<CutDetectLevel, string> = {
  events: "green",
  partial: "gold",
  config_only: "blue",
  none: "default",
}

interface RuleFormValues extends CutTitleRuleInput {
  gameId: number
}

const EMPTY_RULE: Partial<RuleFormValues> = {
  excludedModes: [],
  softExtraCuts: [],
  harshExtraCuts: [],
  detect: "none",
  listStatus: "new",
}

const REFERENCE_ONLY = "Reference only, not applied."

const referenceTitle = (title: string) => (
  <span>{title} <Typography.Text type="secondary">(reference only, not applied)</Typography.Text></span>
)

const renderTextTags = (texts: string[]) => (texts.length ? texts.map(text => <Tag key={text}>{text}</Tag>) : "—")

export function filterCutTitleRules(rules: CutTitleRule[], archetype: number | null, title: string): CutTitleRule[] {
  const needle = title.trim().toLowerCase()

  return rules.filter(r => (archetype === null || r.archetype === archetype)
    && (!needle || r.title.toLowerCase().includes(needle) || String(r.gameId) === needle))
}

export const CutTitleRules: FC = observer(() => {
  const { dataStore: { dataDeliveryStore: store } } = useStore()
  const [form] = Form.useForm<RuleFormValues>()
  const [archetype, setArchetype] = useState<number | null>(null)
  const [title, setTitle] = useState("")
  const [editing, setEditing] = useState<CutTitleRule | null>(null)
  const [open, setOpen] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    store.loadCutTitleRules()
  }, [store])

  const archetypes = useMemo(
    () => Array.from(new Set(store.cutTitleRules.map(r => r.archetype))).sort((a, b) => a - b),
    [store.cutTitleRules],
  )
  const rows = filterCutTitleRules(store.cutTitleRules, archetype, title)

  const openEditor = (rule: CutTitleRule | null) => {
    setEditing(rule)
    setOpen(true)
  }

  const onSave = async() => {
    let values: RuleFormValues
    try {
      values = await form.validateFields()
    } catch {
      return
    }
    const { gameId, ...rule } = values
    setSaving(true)
    const saved = await store.saveCutTitleRule(gameId, {
      title: rule.title,
      archetype: rule.archetype,
      allowedModes: rule.allowedModes ?? "",
      excludedModes: rule.excludedModes ?? [],
      softExtraCuts: rule.softExtraCuts ?? [],
      harshExtraCuts: rule.harshExtraCuts ?? [],
      detect: rule.detect,
      listStatus: rule.listStatus,
    })
    setSaving(false)
    if (saved) setOpen(false)
  }

  const columns = [
    { title: "Game", dataIndex: "gameId", key: "gameId", width: 80 },
    { title: "Title", dataIndex: "title", key: "title" },
    { title: "Archetype", dataIndex: "archetype", key: "archetype", width: 90 },
    { title: referenceTitle("Allowed modes"), dataIndex: "allowedModes", key: "allowedModes" },
    {
      title: referenceTitle("Excluded modes"),
      key: "excludedModes",
      render: (_: unknown, r: CutTitleRule) => renderTextTags(r.excludedModes),
    },
    {
      title: referenceTitle("Soft extra cuts"),
      key: "softExtraCuts",
      render: (_: unknown, r: CutTitleRule) => renderTextTags(r.softExtraCuts),
    },
    {
      title: referenceTitle("Harsh extra cuts"),
      key: "harshExtraCuts",
      render: (_: unknown, r: CutTitleRule) => renderTextTags(r.harshExtraCuts),
    },
    {
      title: "Detect",
      key: "detect",
      render: (_: unknown, r: CutTitleRule) => <Tag color={DETECT_COLOR[r.detect]}>{r.detect}</Tag>,
    },
    { title: "List", dataIndex: "listStatus", key: "listStatus" },
    {
      title: "Updated",
      key: "updatedAt",
      render: (_: unknown, r: CutTitleRule) => new Date(r.updatedAt).toLocaleString(),
    },
    {
      title: "",
      key: "actions",
      render: (_: unknown, r: CutTitleRule) => (
        <Space>
          <Button size="small" onClick={() => openEditor(r)}>Edit</Button>
          <Popconfirm title={`Delete the rule for ${r.title}?`} onConfirm={() => store.deleteCutTitleRule(r.gameId)}>
            <Button size="small" danger>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <Card
      title="Cut title rules"
      extra={<Button type="primary" onClick={() => openEditor(null)}>Add rule</Button>}
    >
      <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
        The gameplay cut reads only the archetype. Allowed and excluded modes and the extra cuts are stored for reference
        and not applied; gate a disallowed mode with a cut void flag.
      </Typography.Paragraph>
      <Space style={{ marginBottom: 12 }}>
        <Select
          allowClear
          placeholder="Archetype"
          style={{ width: 140 }}
          value={archetype ?? undefined}
          onChange={(v?: number) => setArchetype(v ?? null)}
          options={archetypes.map(a => ({ value: a, label: `Archetype ${a}` }))}
        />
        <Input.Search
          allowClear
          placeholder="Title or game id"
          style={{ width: 260 }}
          value={title}
          onChange={e => setTitle(e.target.value)}
        />
      </Space>
      <Table
        size="small"
        rowKey="gameId"
        loading={store.isLoading}
        dataSource={rows}
        columns={columns}
        pagination={{ pageSize: 50 }}
      />

      <Modal
        open={open}
        title={editing ? `Edit rule — ${editing.title}` : "Add cut title rule"}
        okText="Save"
        confirmLoading={saving}
        onOk={onSave}
        onCancel={() => setOpen(false)}
        width={720}
        destroyOnClose
      >
        <Form form={form} layout="vertical" size="small" preserve={false} initialValues={editing ?? EMPTY_RULE}>
          <Space align="start">
            <Form.Item
              name="gameId"
              label="Game id"
              rules={[
                { required: true },
                {
                  validator: (_, gameId?: number) => (!editing && store.cutTitleRules.some(r => r.gameId === gameId)
                    ? Promise.reject(new Error("A rule for this game exists; edit it instead."))
                    : Promise.resolve()),
                },
              ]}
            >
              <InputNumber min={1} precision={0} disabled={!!editing} />
            </Form.Item>
            <Form.Item name="title" label="Title" rules={[{ required: true, whitespace: true, max: 200 }]}>
              <Input style={{ width: 300 }} />
            </Form.Item>
            <Form.Item name="archetype" label="Archetype" rules={[{ required: true }]}>
              <InputNumber min={1} max={15} precision={0} />
            </Form.Item>
          </Space>
          <Form.Item name="allowedModes" label="Allowed modes" extra={REFERENCE_ONLY}>
            <Input.TextArea rows={2} maxLength={2000} />
          </Form.Item>
          <Form.Item name="excludedModes" label="Excluded modes" extra={REFERENCE_ONLY}>
            <Select mode="tags" />
          </Form.Item>
          <Form.Item name="softExtraCuts" label="Soft extra cuts" extra={REFERENCE_ONLY}>
            <Select mode="tags" />
          </Form.Item>
          <Form.Item name="harshExtraCuts" label="Harsh extra cuts" extra={REFERENCE_ONLY}>
            <Select mode="tags" />
          </Form.Item>
          <Space>
            <Form.Item name="detect" label="Detect" rules={[{ required: true }]}>
              <Select style={{ width: 160 }} options={CUT_DETECT_LEVELS.map(v => ({ value: v, label: v }))} />
            </Form.Item>
            <Form.Item name="listStatus" label="List status" rules={[{ required: true }]}>
              <Select style={{ width: 160 }} options={CUT_LIST_STATUSES.map(v => ({ value: v, label: v }))} />
            </Form.Item>
          </Space>
        </Form>
      </Modal>
    </Card>
  )
})
